import React from 'react';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { MedicationFormData, frequencyOptions, timeOptions } from '@/components/medication/types/medication';
import { Sun, Clock, Moon, Plus, Minus, X } from 'lucide-react';
import AnimatedContainer from '@/components/ui-elements/AnimatedContainer';
import { cn } from '@/lib/utils';

interface ScheduleStepProps {
  formData: MedicationFormData;
  updateFormData: (fieldName: keyof MedicationFormData, value: any) => void;
  handleTimeSelection: (timeKey: string) => void;
  decrementDose: (timeKey: string) => void;
  removeDoseTime: (timeKey: string) => void;
}

/**
 * Step 2: Configure medication schedule including frequency and time of day
 */
const ScheduleStep: React.FC<ScheduleStepProps> = ({ 
  formData, 
  updateFormData, 
  handleTimeSelection,
  decrementDose,
  removeDoseTime
}) => {
  // Helper function to get the appropriate icon component
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Sun': return <Sun size={16} />;
      case 'Clock': return <Clock size={16} />;
      case 'Moon': return <Moon size={16} />;
      default: return null;
    }
  };

  return (
    <AnimatedContainer animation="fade-in" className="space-y-6">
      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Frequency <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {frequencyOptions.map((option) => (
            <div
              key={option.value}
              className={`h-12 flex items-center justify-center rounded-md transition-colors px-3 ${formData.frequency === option.value 
                ? 'bg-purple-600 text-white border border-purple-600' 
                : 'bg-gray-50 border border-gray-200 dark:bg-gray-900/10 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-900/20 cursor-pointer'}`}
              onClick={() => updateFormData('frequency', option.value)}
              role="button"
              aria-pressed={formData.frequency === option.value}
              tabIndex={0}
            >
              {option.label}
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Time of Day <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {timeOptions.map((timeOption) => (
            <div key={timeOption.value} className="rounded-lg">
              <Button
                type="button"
                variant={formData.timeOfDay?.includes(timeOption.value) ? "secondary" : "outline"}
                className={cn(
                  "w-full justify-start gap-2 h-auto py-3 px-3", 
                  formData.timeOfDay?.includes(timeOption.value) ? "bg-secondary/50" : ""
                )}
                onClick={() => handleTimeSelection(timeOption.value)}
              >
                {getIconComponent(timeOption.icon)}
                <span>{timeOption.label}</span>
              </Button>
              
              {formData.timeOfDay?.includes(timeOption.value) && (
                <div className="mt-2 flex items-center justify-between px-1">
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-6 w-6 rounded-full"
                      onClick={() => decrementDose(timeOption.value)}
                      disabled={(formData.dosesAtTime?.[timeOption.value] || 0) <= 1}
                    >
                      <Minus size={12} />
                    </Button>
                    <span className="mx-2 text-sm font-medium">
                      {formData.dosesAtTime?.[timeOption.value] || 1} {(formData.dosesAtTime?.[timeOption.value] || 1) === 1 ? 'dose' : 'doses'}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-6 w-6 rounded-full"
                      onClick={() => handleTimeSelection(timeOption.value)}
                    >
                      <Plus size={12} />
                    </Button>
                  </div>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 rounded-full text-muted-foreground hover:text-destructive"
                    onClick={() => removeDoseTime(timeOption.value)}
                  >
                    <X size={12} />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AnimatedContainer>
  );
};

export default ScheduleStep;
